/**
 * API Client for SystemCraft Backend
 * Handles communication with FastAPI backend
 */
import { getToken } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

class ApiClient {
    /**
     * Get headers with optional authentication
     */
    getHeaders(includeAuth = true) {
        const headers = {
            'Content-Type': 'application/json',
        };

        if (includeAuth) {
            const token = getToken();
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
        }

        return headers;
    }

    /**
     * Save design to backend
     * @param {Object} options
     * @param {string} options.mode - 'image' or 'json'
     * @param {string} options.imageData - Base64 encoded image (for image mode)
     * @param {Object} options.designData - JSON design data (for json mode)
     * @param {string} options.questionId - Question ID for evaluation context
     * @returns {Promise<Object>} Save response with design_id
     */
    async saveDesign({ mode, imageData, designData, questionId = 'url-shortener' }) {
        const response = await fetch(`${API_BASE_URL}/api/save-design`, {
            method: 'POST',
            headers: this.getHeaders(true),
            body: JSON.stringify({
                mode,
                image_data: imageData,
                design_data: designData,
                question_id: questionId,
            }),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to save design');
        }

        const result = await response.json();
        return result;
    }

    /**
     * Evaluate design using Gemini AI
     * @param {Object} options
     * @param {string} options.mode - 'image' or 'json'
     * @param {string} options.imageData - Base64 encoded image (for image mode)
     * @param {Object} options.designData - JSON design data (for json mode)
     * @param {string} options.apiKey - Gemini API key
     * @param {string} options.questionId - Question ID for evaluation context
     * @returns {Promise<Object>} Evaluation results
     */
    async evaluateDesign({ mode, imageData, designData, apiKey, questionId = 'url-shortener', designId = null }) {
        const body = {
            mode,
            image_data: imageData,
            design_data: designData,
            api_key: apiKey,
            question_id: questionId,
        };
        if (designId) body.design_id = designId;

        const response = await fetch(`${API_BASE_URL}/api/evaluate`, {
            method: 'POST',
            headers: this.getHeaders(true),
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            const detail = data.detail;
            const message = typeof detail === 'object' ? (detail?.message || 'Failed to evaluate design') : (detail || 'Failed to evaluate design');
            const err = new Error(message);
            if (typeof detail === 'object' && detail?.code === 'GEMINI_NEED_USER_KEY') {
                err.requireUserKey = true;
            }
            throw err;
        }

        const result = await response.json();
        return result;
    }

    /**
     * Get user's saved designs
     * @param {number} skip - Number of designs to skip
     * @param {number} limit - Number of designs to return
     * @returns {Promise<Object>} List of user's designs
     */
    async getUserDesigns(skip = 0, limit = 20) {
        const response = await fetch(`${API_BASE_URL}/api/designs?skip=${skip}&limit=${limit}`, {
            headers: this.getHeaders(true),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to get user designs');
        }

        return await response.json();
    }

    /**
     * Get saved design by ID
     * @param {string} designId - Design ID to retrieve
     * @returns {Promise<Object>} Design metadata
     */
    async getDesign(designId) {
        const response = await fetch(`${API_BASE_URL}/api/designs/${designId}`, {
            headers: this.getHeaders(true),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to get design');
        }

        return await response.json();
    }

    /**
     * Save evaluation result to database
     * @param {string} designId - Design ID that was evaluated
     * @param {string} questionId - Question ID
     * @param {Object} result - Evaluation result object
     * @param {Object} designData - Optional design data at time of evaluation
     * @returns {Promise<Object>} Save response
     */
    async saveEvaluation(designId, questionId, result, designData = null) {
        const formData = new FormData();
        formData.append('design_id', designId);
        formData.append('question_id', questionId);
        formData.append('result', JSON.stringify(result));

        // Include design data if provided
        if (designData) {
            formData.append('design_data', JSON.stringify(designData));
        }

        const response = await fetch(`${API_BASE_URL}/api/evaluations`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${getToken()}`,
            },
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to save evaluation');
        }

        const saveResult = await response.json();
        return saveResult;
    }

    /**
     * Get latest evaluation for a question
     * @param {string} questionId - Question ID
     * @returns {Promise<Object>} Latest evaluation or null
     */
    async getLatestEvaluation(questionId) {
        try {
            const response = await fetch(`${API_BASE_URL}/api/evaluations/latest?question_id=${questionId}`, {
                headers: this.getHeaders(true),
            });

            if (!response.ok) {
                return null;
            }

            return await response.json();
        } catch {
            return null;
        }
    }

    /**
     * Submit user feedback
     * @param {number} rating - Star rating (1-5)
     * @param {string} feedbackText - Optional feedback text
     * @returns {Promise<Object>} Response
     */
    async submitFeedback(rating, feedbackText = '') {
        const formData = new FormData();
        formData.append('rating', rating);
        if (feedbackText) {
            formData.append('feedback_text', feedbackText);
        }

        const response = await fetch(`${API_BASE_URL}/api/feedback`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${getToken()}`,
            },
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to submit feedback');
        }

        const result = await response.json();
        return result;
    }

    /**
     * Get all available questions
     * @returns {Promise<Object>} List of questions
     */
    async getQuestions() {
        const response = await fetch(`${API_BASE_URL}/api/questions`);

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to get questions');
        }

        return await response.json();
    }

    /**
     * Generic GET request
     * @param {string} endpoint - API endpoint to call
     * @returns {Promise<Object>} Response data
     */
    async get(endpoint) {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            headers: this.getHeaders(true),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Request failed');
        }

        return await response.json();
    }

    /**
     * Health check
     * @returns {Promise<Object>} API status
     */
    async healthCheck() {
        const response = await fetch(`${API_BASE_URL}/`);
        return await response.json();
    }
}

export default new ApiClient();
